import {
  Fragment,
  useEffect,
  useMemo,
  useState,
} from "react";

import {
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";

import {
  api,
  formatApiErrorDetail,
} from "@/lib/api";

import {
  useFarm,
} from "@/context/FarmContext";

import {
  formatCurrency,
  getCurrencySymbol,
} from "@/lib/currency";

import {
  PageHeader,
  NoFarm,
} from "@/components/Page";

import {
  Card,
} from "@/components/ui/card";

import {
  Button,
} from "@/components/ui/button";

import {
  Input,
} from "@/components/ui/input";

import {
  Label,
} from "@/components/ui/label";

import {
  Badge,
} from "@/components/ui/badge";

import {
  Textarea,
} from "@/components/ui/textarea";

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";

import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";

import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

import {
  toast,
} from "sonner";

import {
  ScanLabelDialog,
} from "@/modules/ScanLabelDialog";

import {
  Plus,
  Pencil,
  Trash2,
  ChevronDown,
  ChevronRight,
  FlaskConical,
  FileText,
  Leaf,
  Search,
  Filter,
  Package,
  Scale,
  Euro,
  Wheat,
  BookOpen,
  History,
  CalendarDays,
} from "lucide-react";


const BASIC = [
  {
    k: "tonnes_fresh",
    label: "Tonnes fresh",
    unit: "t",
  },
  {
    k: "dm_pct",
    label: "Dry matter",
    unit: "%",
  },
  {
    k: "cp_pct",
    label: "Crude protein",
    unit: "%",
  },
  {
    k: "me",
    label: "ME",
    unit: "MJ/kg DM",
  },
  {
    k: "ndf_pct",
    label: "NDF",
    unit: "%",
  },
  {
    k: "starch_pct",
    label: "Starch",
    unit: "%",
  },
  {
    k: "cost_per_tonne",
    label: "Cost",
    unit: "currency_per_tonne",
  },
];


const ADV_GROUPS = [
  {
    title: "Protein fractions",
    fields: [
      {
        k: "soluble_protein_pct_cp",
        label: "Soluble protein",
        unit: "% CP",
      },
      {
        k: "rdp_pct_cp",
        label: "RDP",
        unit: "% CP",
      },
      {
        k: "rup_pct_cp",
        label: "RUP",
        unit: "% CP",
      },
      {
        k: "ndicp_pct",
        label: "NDICP",
        unit: "% DM",
      },
      {
        k: "adicp_pct",
        label: "ADICP",
        unit: "% DM",
      },
    ],
  },

  {
    title: "Fibre fractions",
    fields: [
      {
        k: "adf_pct",
        label: "ADF",
        unit: "% DM",
      },
      {
        k: "lignin_pct",
        label: "Lignin",
        unit: "% DM",
      },
      {
        k: "andfom_pct",
        label: "aNDFom",
        unit: "% DM",
      },
      {
        k: "pendf_pct",
        label: "peNDF",
        unit: "% DM",
      },
      {
        k: "ash_pct",
        label: "Ash",
        unit: "% DM",
      },
    ],
  },

  {
    title: "Sugars & carbohydrates",
    fields: [
      {
        k: "sugar_pct",
        label: "Sugar (WSC)",
        unit: "% DM",
      },
      {
        k: "nfc_pct",
        label: "NFC",
        unit: "% DM",
      },
      {
        k: "soluble_fibre_pct",
        label: "Soluble fibre",
        unit: "% DM",
      },
    ],
  },

  {
    title: "Fat",
    fields: [
      {
        k: "ee_pct",
        label: "Ether Extract",
        unit: "% DM",
      },
    ],
  },

  {
    title: "Minerals — macro",
    fields: [
      {
        k: "ca_pct",
        label: "Calcium",
        unit: "% DM",
      },
      {
        k: "p_pct",
        label: "Phosphorus",
        unit: "% DM",
      },
      {
        k: "mg_pct",
        label: "Magnesium",
        unit: "% DM",
      },
      {
        k: "k_pct",
        label: "Potassium",
        unit: "% DM",
      },
      {
        k: "na_pct",
        label: "Sodium",
        unit: "% DM",
      },
      {
        k: "s_pct",
        label: "Sulphur",
        unit: "% DM",
      },
      {
        k: "cl_pct",
        label: "Chloride",
        unit: "% DM",
      },
    ],
  },

  {
    title: "Minerals — trace",
    fields: [
      {
        k: "cu_mg",
        label: "Copper",
        unit: "mg/kg",
      },
      {
        k: "zn_mg",
        label: "Zinc",
        unit: "mg/kg",
      },
      {
        k: "mn_mg",
        label: "Manganese",
        unit: "mg/kg",
      },
      {
        k: "se_mg",
        label: "Selenium",
        unit: "mg/kg",
      },
      {
        k: "i_mg",
        label: "Iodine",
        unit: "mg/kg",
      },
      {
        k: "co_mg",
        label: "Cobalt",
        unit: "mg/kg",
      },
      {
        k: "fe_mg",
        label: "Iron",
        unit: "mg/kg",
      },
      {
        k: "mo_mg",
        label: "Molybdenum",
        unit: "mg/kg",
      },
    ],
  },

  {
    title: "Fermentation (silages)",
    fields: [
      {
        k: "ph",
        label: "pH",
        unit: "",
      },
      {
        k: "lactic_acid_pct",
        label: "Lactic acid",
        unit: "% DM",
      },
      {
        k: "acetic_acid_pct",
        label: "Acetic acid",
        unit: "% DM",
      },
      {
        k: "butyric_acid_pct",
        label: "Butyric acid",
        unit: "% DM",
      },
      {
        k: "ammonia_n_pct",
        label: "Ammonia-N",
        unit: "% total N",
      },
    ],
  },

  {
    title: "Digestibility",
    fields: [
      {
        k: "dmd_pct",
        label: "DMD",
        unit: "%",
      },
      {
        k: "domd_pct",
        label: "DOMD",
        unit: "%",
      },
      {
        k: "omd_pct",
        label: "OMD",
        unit: "%",
      },
      {
        k: "ndfd_pct",
        label: "NDF digestibility",
        unit: "%",
      },
    ],
  },
];


const ADV_KEYS =
  ADV_GROUPS.flatMap(
    (group) =>
      group.fields.map(
        (field) =>
          field.k
      )
  );


const EMPTY = {
  name: "",
  category: "",
  notes: "",
  tonnes_fresh: 0,
  dm_pct: 0,
  cp_pct: 0,
  me: 0,
  ndf_pct: 0,
  starch_pct: 0,
  cost_per_tonne: 0,
};


const NEW =
  "__new__";


const CONF = {
  high: {
    cls:
      "bg-primary/5",

    chip:
      "bg-primary/10 text-primary",
  },

  medium: {
    cls:
      "bg-[hsl(43,74%,52%)]/5",

    chip:
      "bg-[hsl(43,74%,52%)]/15 text-[hsl(38,70%,35%)]",
  },

  low: {
    cls:
      "bg-destructive/5",

    chip:
      "bg-destructive/10 text-destructive",
  },
};


function val(value) {
  return (
    value === null ||
    value === undefined ||
    value === ""
      ? "—"
      : value
  );
}


function numberValue(
  value,
  decimals = 0
) {
  const number =
    Number(value);

  if (
    !Number.isFinite(
      number
    )
  ) {
    return "—";
  }

  return number.toLocaleString(
    "en-IE",
    {
      minimumFractionDigits:
        decimals,

      maximumFractionDigits:
        decimals,
    }
  );
}


function todayIsoDate() {
  return new Date()
    .toISOString()
    .slice(0, 10);
}


function buildPayload(
  form
) {
  const payload = {
    name:
      form.name,

    category:
      form.category ||
      null,

    notes:
      form.notes ||
      null,
  };


  BASIC.forEach(
    ({
      k,
    }) => {
      payload[k] =
        form[k] === "" ||
        form[k] == null
          ? 0
          : Number(
              form[k]
            );
    }
  );


  ADV_KEYS.forEach(
    (key) => {
      payload[key] =
        form[key] === "" ||
        form[key] == null
          ? null
          : Number(
              form[key]
            );
    }
  );


  return payload;
}


function categoryStyle(
  feed
) {
  const category =
    (
      feed.category ||
      feed.name ||
      ""
    ).toLowerCase();


  if (
    category.includes(
      "silage"
    ) ||
    category.includes(
      "forage"
    )
  ) {
    return {
      chip:
        "bg-emerald-50 text-emerald-800",

      icon:
        "bg-emerald-100 text-emerald-700",
    };
  }


  if (
    category.includes(
      "barley"
    ) ||
    category.includes(
      "wheat"
    ) ||
    category.includes(
      "cereal"
    )
  ) {
    return {
      chip:
        "bg-amber-50 text-amber-800",

      icon:
        "bg-amber-100 text-amber-700",
    };
  }


  if (
    category.includes(
      "concentrate"
    ) ||
    category.includes(
      "meal"
    ) ||
    category.includes(
      "nut"
    ) ||
    category.includes(
      "hulls"
    )
  ) {
    return {
      chip:
        "bg-violet-50 text-violet-800",

      icon:
        "bg-violet-100 text-violet-700",
    };
  }


  return {
    chip:
      "bg-secondary text-muted-foreground",

    icon:
      "bg-secondary text-primary",
  };
}


function FeedFormFields({
  form,
  set,
  highlight,
  showPriceEffectiveDate = false,
  priceEffectiveDate = "",
  setPriceEffectiveDate,
}) {
  const {
    farm,
  } =
    useFarm();


  const currencySymbol =
    getCurrencySymbol(
      farm?.currency ||
        "EUR"
    );


  const hl =
    (key) =>
      highlight &&
      highlight.has(
        key
      )
        ? "ring-2 ring-primary/50"
        : "";


  return (
    <div className="space-y-5">

      <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">

        <div className="col-span-2 sm:col-span-1">

          <Label className="text-xs">
            Feed name
          </Label>

          <Input
            data-testid="feed-name-input"
            value={
              form.name
            }
            onChange={(
              event
            ) =>
              set(
                "name",
                event.target
                  .value
              )
            }
            required
            className="mt-1"
          />

        </div>


        <div>

          <Label className="text-xs">
            Category
          </Label>

          <Input
            data-testid="feed-category-input"
            value={
              form.category ||
              ""
            }
            onChange={(
              event
            ) =>
              set(
                "category",
                event.target
                  .value
              )
            }
            placeholder="Forage, Cereal…"
            className="mt-1"
          />

        </div>


        {BASIC.map(
          ({
            k,
            label,
            unit,
          }) => (

            <div key={k}>

              <Label className="text-xs">

                {label}

                {unit
                  ? ` (${
                      unit ===
                      "currency_per_tonne"
                        ? `${currencySymbol}/t`
                        : unit
                    })`
                  : ""}

              </Label>

              <Input
                type="number"
                step="any"
                data-testid={`feed-${k}-input`}
                value={
                  form[k]
                }
                onChange={(
                  event
                ) =>
                  set(
                    k,
                    event.target
                      .value
                  )
                }
                className={`mt-1 ${hl(
                  k
                )}`}
              />

            </div>

          )
        )}

      </div>


      {showPriceEffectiveDate && (

        <div className="rounded-xl border bg-secondary/30 p-4">

          <div className="flex items-start gap-3">

            <CalendarDays className="h-4 w-4 text-primary mt-0.5 shrink-0" />

            <div className="flex-1">

              <Label className="text-xs">
                New price effective from
              </Label>

              <Input
                type="date"
                value={
                  priceEffectiveDate
                }
                onChange={(
                  event
                ) =>
                  setPriceEffectiveDate?.(
                    event.target.value
                  )
                }
                className="mt-1 max-w-[220px]"
              />

              <p className="text-[11px] text-muted-foreground mt-2 leading-relaxed">
                If the cost per tonne changes, Farm Hand will use the new price from this date onward. Earlier feed-use records keep the price that applied at the time.
              </p>

            </div>

          </div>

        </div>

      )}


      <Accordion
        type="single"
        collapsible
        defaultValue={
          highlight &&
          highlight.size
            ? "adv"
            : undefined
        }
        className="border rounded-lg px-3"
      >

        <AccordionItem
          value="adv"
          className="border-none"
        >

          <AccordionTrigger
            data-testid="feed-advanced-fields-trigger"
            className="text-sm font-semibold"
          >

            <span className="flex items-center gap-2">

              <FlaskConical className="h-4 w-4 text-primary" />

              Advanced laboratory analysis

            </span>

          </AccordionTrigger>


          <AccordionContent>

            <div className="space-y-5 pt-2">

              {ADV_GROUPS.map(
                (
                  group
                ) => (

                  <div key={
                    group.title
                  }>

                    <p className="text-[11px] font-semibold uppercase tracking-[0.15em] text-muted-foreground mb-2">
                      {group.title}
                    </p>


                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">

                      {group.fields.map(
                        (
                          field
                        ) => (

                          <div key={
                            field.k
                          }>

                            <Label className="text-xs">

                              {field.label}

                              {field.unit
                                ? ` (${field.unit})`
                                : ""}

                            </Label>


                            <Input
                              type="number"
                              step="any"
                              data-testid={`feed-${field.k}-input`}
                              value={
                                form[
                                  field.k
                                ] ??
                                ""
                              }
                              onChange={(
                                event
                              ) =>
                                set(
                                  field.k,
                                  event
                                    .target
                                    .value
                                )
                              }
                              className={`mt-1 ${hl(
                                field.k
                              )}`}
                              placeholder="—"
                            />

                          </div>

                        )
                      )}

                    </div>

                  </div>

                )
              )}

            </div>

          </AccordionContent>

        </AccordionItem>

      </Accordion>


      <div>

        <Label className="text-xs">
          Notes
        </Label>

        <Textarea
          data-testid="feed-notes-input"
          value={
            form.notes ||
            ""
          }
          onChange={(
            event
          ) =>
            set(
              "notes",
              event.target
                .value
            )
          }
          className="mt-1"
          rows={3}
          placeholder="Unmatched lab lines are stored here for traceability."
        />

      </div>

    </div>
  );
}


function SummaryMetric({
  icon: Icon,
  value,
  label,
  hint,
}) {
  return (
    <div className="flex items-center gap-3 min-w-0">

      <div className="h-11 w-11 rounded-full bg-primary/10 grid place-items-center shrink-0">

        <Icon className="h-5 w-5 text-primary" />

      </div>


      <div className="min-w-0">

        <p className="font-display font-extrabold text-lg sm:text-xl text-[hsl(148,38%,16%)] tabular-nums">
          {value}
        </p>

        <p className="text-xs font-semibold text-[hsl(148,38%,16%)]">
          {label}
        </p>

        {hint && (
          <p className="text-[10px] sm:text-xs text-muted-foreground mt-0.5">
            {hint}
          </p>
        )}

      </div>

    </div>
  );
}


export default function FeedInventory() {
  const {
    farmId,
    farm,
    loading,
  } =
    useFarm();


  const currency =
    farm?.currency ||
    "EUR";


  const currencySymbol =
    getCurrencySymbol(
      currency
    );


  const qc =
    useQueryClient();


  const [
    open,
    setOpen,
  ] =
    useState(false);


  const [
    editing,
    setEditing,
  ] =
    useState(null);


  const [
    form,
    setForm,
  ] =
    useState(EMPTY);


  const [
    expanded,
    setExpanded,
  ] =
    useState({});


  const [
    search,
    setSearch,
  ] =
    useState("");


  const [
    stockFilter,
    setStockFilter,
  ] =
    useState("all");


  const [
    sortMode,
    setSortMode,
  ] =
    useState("name");


  const [
    importOpen,
    setImportOpen,
  ] =
    useState(false);


  const [
    importStep,
    setImportStep,
  ] =
    useState("paste");


  const [
    importText,
    setImportText,
  ] =
    useState("");


  const [
    parsed,
    setParsed,
  ] =
    useState(null);


  const [
    targetId,
    setTargetId,
  ] =
    useState(NEW);


  const [
    reviewForm,
    setReviewForm,
  ] =
    useState(EMPTY);


  const [
    parsing,
    setParsing,
  ] =
    useState(false);


  const [
    uploading,
    setUploading,
  ] =
    useState(false);


  const [
    libraryOpen,
    setLibraryOpen,
  ] =
    useState(false);


  const [
    librarySearch,
    setLibrarySearch,
  ] =
    useState("");


  const [
    priceEffectiveDate,
    setPriceEffectiveDate,
  ] =
    useState(
      todayIsoDate()
    );


  const [
    priceHistoryOpen,
    setPriceHistoryOpen,
  ] =
    useState(false);


  const [
    priceHistoryFeed,
    setPriceHistoryFeed,
  ] =
    useState(null);


  const [
    priceHistory,
    setPriceHistory,
  ] =
    useState(null);


  const [
    priceHistoryLoading,
    setPriceHistoryLoading,
  ] =
    useState(false);


  const {
    data: feeds = [],
  } =
    useQuery({
      queryKey: [
        "feeds",
        farmId,
      ],

      queryFn:
        async () =>
          (
            await api.get(
              `/farms/${farmId}/feeds`
            )
          ).data,

      enabled:
        Boolean(
          farmId
        ),

      refetchInterval:
        5 *
        60 *
        1000,

      refetchOnWindowFocus:
        true,
    });


  const {
    data: feedLibrary = [],
  } =
    useQuery({
      queryKey: [
        "feed-library",
      ],

      queryFn:
        async () =>
          (
            await api.get(
              "/feed-library"
            )
          ).data,
    });


  const filteredLibrary =
    useMemo(
      () => {
        const query =
          librarySearch
            .trim()
            .toLowerCase();

        if (!query) {
          return feedLibrary;
        }

        return feedLibrary.filter(
          (
            feed
          ) =>
            (
              feed.name ||
              ""
            )
              .toLowerCase()
              .includes(
                query
              ) ||
            (
              feed.category ||
              ""
            )
              .toLowerCase()
              .includes(
                query
              )
        );
      },
      [
        feedLibrary,
        librarySearch,
      ]
    );


  const grazedGrass =
    feeds.find(
      (
        feed
      ) =>
        feed.is_active_grass
    );


  const storedFeeds =
    feeds.filter(
      (
        feed
      ) =>
        !feed.is_active_grass
    );


  const invalidate =
    () => {
      qc.invalidateQueries({
        queryKey: [
          "feeds",
          farmId,
        ],
      });

      qc.invalidateQueries({
        queryKey: [
          "dashboard",
          farmId,
        ],
      });
    };


  const save =
    useMutation({
      mutationFn:
        async (
          payload
        ) =>
          editing
            ? api.put(
                `/farms/${farmId}/feeds/${editing.id}`,
                payload
              )
            : api.post(
                `/farms/${farmId}/feeds`,
                payload
              ),

      onSuccess:
        () => {
          invalidate();

          setOpen(
            false
          );

          toast.success(
            editing
              ? "Feed updated"
              : "Feed added"
          );
        },

      onError:
        (
          error
        ) =>
          toast.error(
            formatApiErrorDetail(
              error.response
                ?.data
                ?.detail
            )
          ),
    });


  const del =
    useMutation({
      mutationFn:
        async (
          id
        ) =>
          api.delete(
            `/farms/${farmId}/feeds/${id}`
          ),

      onSuccess:
        () => {
          invalidate();

          toast.success(
            "Deleted"
          );
        },
    });


  const openNew =
    () => {
      setEditing(
        null
      );

      setForm(
        EMPTY
      );

      setPriceEffectiveDate(
        todayIsoDate()
      );

      setOpen(
        true
      );
    };


  const openEdit =
    (
      feed
    ) => {
      if (
        feed.is_active_grass
      ) {
        toast.info(
          "Update Grazed Grass through the active Grass Quality analysis."
        );

        return;
      }


      setEditing(
        feed
      );

      setForm({
        ...EMPTY,
        ...feed,
      });

      setPriceEffectiveDate(
        todayIsoDate()
      );

      setOpen(
        true
      );
    };


  const chooseLibraryFeed =
    (
      feed
    ) => {
      setEditing(
        null
      );

      setForm({
        ...EMPTY,

        name:
          feed.name ||
          "",

        category:
          feed.category ||
          "",

        dm_pct:
          feed.dm_pct ??
          0,

        cp_pct:
          feed.cp_pct ??
          0,

        me:
          feed.me ??
          0,

        ndf_pct:
          feed.ndf_pct ??
          0,

        starch_pct:
          feed.starch_pct ??
          0,

        cost_per_tonne:
          feed.cost_per_tonne ??
          0,

        notes:
          "Started from Farm Hand reference feed values. Review and edit for your own farm before saving.",
      });

      setPriceEffectiveDate(
        todayIsoDate()
      );

      setLibraryOpen(
        false
      );

      setOpen(
        true
      );
    };


  const openFeedPriceHistory =
    async (
      feed
    ) => {
      setPriceHistoryFeed(
        feed
      );

      setPriceHistoryOpen(
        true
      );

      setPriceHistoryLoading(
        true
      );

      setPriceHistory(
        null
      );

      try {
        const {
          data,
        } =
          await api.get(
            `/farms/${farmId}/feed-usage/prices/${feed.id}`
          );

        setPriceHistory(
          data
        );

      } catch (error) {
        toast.error(
          formatApiErrorDetail(
            error.response
              ?.data
              ?.detail ||
            "Unable to load price history"
          )
        );

      } finally {
        setPriceHistoryLoading(
          false
        );
      }
    };


  const set =
    (
      key,
      value
    ) =>
      setForm(
        (
          current
        ) => ({
          ...current,
          [key]:
            value,
        })
      );


  const setR =
    (
      key,
      value
    ) =>
      setReviewForm(
        (
          current
        ) => ({
          ...current,
          [key]:
            value,
        })
      );


  const toggle =
    (
      id
    ) =>
      setExpanded(
        (
          current
        ) => ({
          ...current,
          [id]:
            !current[
              id
            ],
        })
      );


  const submit =
    async (
      event
    ) => {
      event.preventDefault();

      const payload =
        buildPayload(
          form
        );

      if (!editing) {
        save.mutate(
          payload
        );

        return;
      }

      const oldPrice =
        Number(
          editing.cost_per_tonne
        ) ||
        0;

      const newPrice =
        Number(
          form.cost_per_tonne
        ) ||
        0;

      const priceChanged =
        oldPrice !==
        newPrice;

      if (!priceChanged) {
        save.mutate(
          payload
        );

        return;
      }

      /*
       * Do not directly overwrite the current feed price
       * before the effective-dated price endpoint has
       * decided which price applies today.
       */
      payload.cost_per_tonne =
        oldPrice;

      try {
        await api.put(
          `/farms/${farmId}/feeds/${editing.id}`,
          payload
        );

        await api.post(
          `/farms/${farmId}/feed-usage/prices`,
          {
            feed_id:
              editing.id,

            cost_per_tonne:
              newPrice,

            effective_date:
              priceEffectiveDate ||
              todayIsoDate(),

            notes:
              "Price updated from Feed Inventory",
          }
        );

        invalidate();

        qc.invalidateQueries({
          queryKey: [
            "feed-usage-history",
            farmId,
          ],
        });

        setOpen(
          false
        );

        toast.success(
          "Feed and price history updated"
        );

      } catch (error) {
        toast.error(
          formatApiErrorDetail(
            error.response
              ?.data
              ?.detail
          )
        );
      }
    };


  const openImport =
    () => {
      setImportStep(
        "paste"
      );

      setImportText(
        ""
      );

      setParsed(
        null
      );

      setTargetId(
        NEW
      );

      setImportOpen(
        true
      );
    };


  const runParse =
    async () => {
      if (
        !importText.trim()
      ) {
        toast.error(
          "Paste a lab report first"
        );

        return;
      }


      setParsing(
        true
      );


      try {
        const {
          data,
        } =
          await api.post(
            `/farms/${farmId}/feeds/parse-lab-report`,
            {
              text:
                importText,
            }
          );


        if (
          data.matched_count ===
          0
        ) {
          toast.warning(
            "No nutrients recognised — check the report format"
          );
        }


        setParsed(
          data
        );

        setImportStep(
          "review"
        );

      } catch (error) {
        toast.error(
          formatApiErrorDetail(
            error.response
              ?.data
              ?.detail
          )
        );

      } finally {
        setParsing(
          false
        );
      }
    };


  useEffect(() => {
    if (
      !parsed
    ) {
      return;
    }


    const base =
      targetId === NEW
        ? EMPTY
        : (
            feeds.find(
              (
                feed
              ) =>
                feed.id ===
                targetId
            ) ||
            EMPTY
          );


    const unmatchedNote =
      parsed.unmatched
        .length
        ? `Unmatched lab lines:\n${parsed.unmatched.join(
            "\n"
          )}`
        : "";


    const notes = [
      base.notes,
      unmatchedNote,
    ]
      .filter(
        Boolean
      )
      .join(
        "\n\n"
      );


    const name =
      targetId === NEW
        ? (
            parsed.name ||
            ""
          )
        : (
            base.name ||
            parsed.name ||
            ""
          );


    setReviewForm({
      ...EMPTY,
      ...base,
      ...parsed.mapped,
      name,
      notes,
    });

  }, [
    parsed,
    targetId,
  ]); // eslint-disable-line


  const handleFile =
    async (
      event
    ) => {
      const file =
        event.target
          .files?.[0];


      event.target.value =
        "";


      if (!file) {
        return;
      }


      if (
        file.type !==
          "application/pdf" &&
        !file.name
          .toLowerCase()
          .endsWith(
            ".pdf"
          )
      ) {
        toast.error(
          "Please upload a PDF file"
        );

        return;
      }


      setUploading(
        true
      );


      try {
        const fd =
          new FormData();


        fd.append(
          "file",
          file
        );


        const {
          data,
        } =
          await api.post(
            `/farms/${farmId}/feeds/parse-lab-report/pdf`,
            fd
          );


        if (
          data.matched_count ===
          0
        ) {
          toast.warning(
            "No nutrients recognised in the PDF — review or paste text"
          );
        }


        setParsed(
          data
        );

        setImportStep(
          "review"
        );

      } catch (error) {
        toast.error(
          formatApiErrorDetail(
            error.response
              ?.data
              ?.detail
          )
        );

      } finally {
        setUploading(
          false
        );
      }
    };


  const importSave =
    async () => {
      if (
        !reviewForm.name
      ) {
        toast.error(
          "Give the feed a name"
        );

        return;
      }


      const payload =
        buildPayload(
          reviewForm
        );


      if (
        parsed?.source
      ) {
        payload.lab_source_filename =
          parsed.source.filename;

        payload.lab_import_date =
          parsed.source.uploaded_at;
      }


      try {
        if (
          targetId ===
          NEW
        ) {
          await api.post(
            `/farms/${farmId}/feeds`,
            payload
          );

        } else {
          await api.put(
            `/farms/${farmId}/feeds/${targetId}`,
            payload
          );
        }


        invalidate();

        setImportOpen(
          false
        );


        toast.success(
          targetId === NEW
            ? "Feed created from lab report"
            : "Feed updated from lab report"
        );

      } catch (error) {
        toast.error(
          formatApiErrorDetail(
            error.response
              ?.data
              ?.detail
          )
        );
      }
    };


  const highlight =
    parsed
      ? new Set(
          Object.keys(
            parsed.mapped
          )
        )
      : new Set();


  const hasAdvanced =
    (
      feed
    ) =>
      ADV_KEYS.some(
        (
          key
        ) =>
          feed[key] !==
            null &&
          feed[key] !==
            undefined
      );


  // -----------------------------------------------------
  // Inventory summary
  // Stored feeds and pasture are now deliberately separate
  // -----------------------------------------------------

  const summary =
    useMemo(
      () => {
        const feedsInStock =
          storedFeeds.filter(
            (
              feed
            ) =>
              Number(
                feed.tonnes_fresh
              ) > 0
          ).length;


        const storedDmKg =
          storedFeeds.reduce(
            (
              total,
              feed
            ) => {
              const tonnesFresh =
                Number(
                  feed.tonnes_fresh
                ) ||
                0;


              const dmPct =
                Number(
                  feed.dm_pct
                ) ||
                0;


              return (
                total +
                tonnesFresh *
                  1000 *
                  (
                    dmPct /
                    100
                  )
              );
            },
            0
          );


        const pastureDmKg =
          Number(
            grazedGrass
              ?.available_grass_kg_dm
          ) ||
          0;


        const storedFreshTonnes =
          storedFeeds.reduce(
            (
              total,
              feed
            ) =>
              total +
              (
                Number(
                  feed.tonnes_fresh
                ) ||
                0
              ),
            0
          );


        const inventoryValue =
          storedFeeds.reduce(
            (
              total,
              feed
            ) => {
              const tonnes =
                Number(
                  feed.tonnes_fresh
                ) ||
                0;


              const cost =
                Number(
                  feed.cost_per_tonne
                ) ||
                0;


              return (
                total +
                tonnes *
                  cost
              );
            },
            0
          );


        return {
          totalFeeds:
            storedFeeds.length,

          feedsInStock,

          storedDmKg,

          pastureDmKg,

          storedFreshTonnes,

          inventoryValue,
        };
      },
      [
        storedFeeds,
        grazedGrass,
      ]
    );


  const filteredFeeds =
    useMemo(
      () => {
        let result =
          [
            ...storedFeeds,
          ];


        const query =
          search
            .trim()
            .toLowerCase();


        if (
          query
        ) {
          result =
            result.filter(
              (
                feed
              ) =>
                (
                  feed.name ||
                  ""
                )
                  .toLowerCase()
                  .includes(
                    query
                  ) ||
                (
                  feed.category ||
                  ""
                )
                  .toLowerCase()
                  .includes(
                    query
                  )
            );
        }


        if (
          stockFilter ===
          "stock"
        ) {
          result =
            result.filter(
              (
                feed
              ) =>
                Number(
                  feed.tonnes_fresh
                ) > 0
            );
        }


        if (
          stockFilter ===
          "empty"
        ) {
          result =
            result.filter(
              (
                feed
              ) =>
                Number(
                  feed.tonnes_fresh
                ) <= 0
            );
        }


        if (
          sortMode ===
          "name"
        ) {
          result.sort(
            (
              a,
              b
            ) =>
              (
                a.name ||
                ""
              ).localeCompare(
                b.name ||
                  ""
              )
          );
        }


        if (
          sortMode ===
          "stock"
        ) {
          result.sort(
            (
              a,
              b
            ) =>
              Number(
                b.tonnes_fresh
              ) -
              Number(
                a.tonnes_fresh
              )
          );
        }


        if (
          sortMode ===
          "cost"
        ) {
          result.sort(
            (
              a,
              b
            ) =>
              Number(
                b.cost_per_tonne
              ) -
              Number(
                a.cost_per_tonne
              )
          );
        }


        return result;
      },
      [
        storedFeeds,
        search,
        stockFilter,
        sortMode,
      ]
    );


  if (
    !loading &&
    !farmId
  ) {
    return (
      <NoFarm />
    );
  }


  return (
    <div>

      <PageHeader
        title="Feed Inventory"
        subtitle="Manage your feed stocks and nutritional information."
        action={
          <div className="flex flex-wrap gap-2">

            <ScanLabelDialog
              farmId={
                farmId
              }
              feeds={
                feeds
              }
              onSaved={
                invalidate
              }
            />


            <Button
              variant="outline"
              className="rounded-full"
              onClick={() =>
                setLibraryOpen(
                  true
                )
              }
            >

              <BookOpen className="h-4 w-4 mr-1.5" />

              <span className="hidden sm:inline">
                Reference library
              </span>

              <span className="sm:hidden">
                Library
              </span>

            </Button>


            <Button
              variant="outline"
              className="rounded-full"
              data-testid="import-lab-report-button"
              onClick={
                openImport
              }
            >

              <FileText className="h-4 w-4 mr-1.5" />

              <span className="hidden sm:inline">
                Import lab report
              </span>

              <span className="sm:hidden">
                Lab report
              </span>

            </Button>


            <Dialog
              open={
                open
              }
              onOpenChange={
                setOpen
              }
            >

              <DialogTrigger
                asChild
              >

                <Button
                  className="rounded-full"
                  data-testid="feed-add-button"
                  onClick={
                    openNew
                  }
                >

                  <Plus className="h-4 w-4 mr-1" />

                  Add feed

                </Button>

              </DialogTrigger>


              <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">

                <DialogHeader>

                  <DialogTitle>
                    {editing
                      ? "Edit"
                      : "Add"}{" "}
                    feed
                  </DialogTitle>

                  <DialogDescription>
                    Enter basic values used in rations. Advanced laboratory fields are optional.
                  </DialogDescription>

                </DialogHeader>


                <form
                  onSubmit={
                    submit
                  }
                >

                  <FeedFormFields
                    form={
                      form
                    }
                    set={
                      set
                    }
                    showPriceEffectiveDate={
                      Boolean(
                        editing
                      )
                    }
                    priceEffectiveDate={
                      priceEffectiveDate
                    }
                    setPriceEffectiveDate={
                      setPriceEffectiveDate
                    }
                  />


                  <DialogFooter className="mt-5">

                    <Button
                      type="submit"
                      className="rounded-full"
                      data-testid="feed-save-button"
                      disabled={
                        save.isPending
                      }
                    >

                      {editing
                        ? "Save"
                        : "Add"}

                    </Button>

                  </DialogFooter>

                </form>

              </DialogContent>

            </Dialog>

          </div>
        }
      />


      {/* ------------------------------------------------ */}
      {/* Inventory summary                                */}
      {/* ------------------------------------------------ */}

      <Card className="mb-5 p-4 sm:p-5">

        <div className="grid grid-cols-2 lg:grid-cols-6 gap-5 lg:divide-x">

          <SummaryMetric
            icon={
              Package
            }
            value={
              summary.totalFeeds
            }
            label="Stored feeds"
            hint="Feed types"
          />


          <div className="lg:pl-5">

            <SummaryMetric
              icon={
                Wheat
              }
              value={
                summary.feedsInStock
              }
              label="Feeds in stock"
              hint="Above 0 tonnes"
            />

          </div>


          <div className="lg:pl-5">

            <SummaryMetric
              icon={
                Scale
              }
              value={`${numberValue(
                summary.storedDmKg
              )} kg`}
              label="Stored feed DM"
              hint="Physical inventory"
            />

          </div>


          <div className="lg:pl-5">

            <SummaryMetric
              icon={
                Leaf
              }
              value={`${numberValue(
                summary.pastureDmKg
              )} kg`}
              label="Pasture DM"
              hint="Live grazed grass"
            />

          </div>


          <div className="lg:pl-5">

            <SummaryMetric
              icon={
                Package
              }
              value={`${numberValue(
                summary.storedFreshTonnes,
                1
              )} t`}
              label="Stored fresh feed"
              hint="Physical inventory"
            />

          </div>


          <div className="col-span-2 lg:col-span-1 lg:pl-5">

            <SummaryMetric
              icon={
                Euro
              }
              value={formatCurrency(
                summary.inventoryValue,
                currency,
                0
              )}
              label="Inventory value"
              hint="Stored feeds only"
            />

          </div>

        </div>

      </Card>


      {/* ------------------------------------------------ */}
      {/* Reference feed library                           */}
      {/* ------------------------------------------------ */}

      <Dialog
        open={
          libraryOpen
        }
        onOpenChange={
          setLibraryOpen
        }
      >

        <DialogContent className="max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">

          <DialogHeader>

            <DialogTitle>
              Reference Feed Library
            </DialogTitle>

            <DialogDescription>
              Start with standard reference values, then review and edit them for the feed actually used on your farm.
            </DialogDescription>

          </DialogHeader>


          <div className="relative mt-1">

            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />

            <Input
              value={
                librarySearch
              }
              onChange={(
                event
              ) =>
                setLibrarySearch(
                  event.target.value
                )
              }
              placeholder="Search reference feeds..."
              className="pl-9"
            />

          </div>


          <div className="overflow-y-auto pr-1 mt-4">

            {filteredLibrary.length ===
              0 ? (

              <div className="py-12 text-center text-sm text-muted-foreground">
                No reference feeds match your search.
              </div>

            ) : (

              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">

                {filteredLibrary.map(
                  (
                    feed
                  ) => {
                    const style =
                      categoryStyle(
                        feed
                      );

                    return (

                      <Card
                        key={
                          feed.name
                        }
                        className="p-4"
                      >

                        <div className="flex items-start justify-between gap-3">

                          <div className="min-w-0">

                            <div className="flex flex-wrap items-center gap-2">

                              <div className={`h-9 w-9 rounded-full grid place-items-center shrink-0 ${style.icon}`}>
                                <Wheat className="h-4 w-4" />
                              </div>

                              <div>

                                <p className="font-semibold text-[hsl(148,38%,16%)]">
                                  {feed.name}
                                </p>

                                {feed.category && (

                                  <Badge
                                    variant="secondary"
                                    className={`text-[9px] border-none ${style.chip}`}
                                  >
                                    {feed.category}
                                  </Badge>

                                )}

                              </div>

                            </div>

                          </div>


                          <Button
                            size="sm"
                            className="rounded-full shrink-0"
                            onClick={() =>
                              chooseLibraryFeed(
                                feed
                              )
                            }
                          >

                            <Plus className="h-3.5 w-3.5 mr-1" />
                            Use

                          </Button>

                        </div>


                        <div className="grid grid-cols-3 sm:grid-cols-5 gap-3 mt-4">

                          <div>
                            <p className="text-[9px] uppercase tracking-wider text-muted-foreground">
                              DM
                            </p>
                            <p className="text-sm font-semibold">
                              {numberValue(
                                feed.dm_pct,
                                1
                              )}
                              %
                            </p>
                          </div>

                          <div>
                            <p className="text-[9px] uppercase tracking-wider text-muted-foreground">
                              CP
                            </p>
                            <p className="text-sm font-semibold">
                              {numberValue(
                                feed.cp_pct,
                                1
                              )}
                              %
                            </p>
                          </div>

                          <div>
                            <p className="text-[9px] uppercase tracking-wider text-muted-foreground">
                              ME
                            </p>
                            <p className="text-sm font-semibold">
                              {numberValue(
                                feed.me,
                                1
                              )}
                            </p>
                          </div>

                          <div>
                            <p className="text-[9px] uppercase tracking-wider text-muted-foreground">
                              NDF
                            </p>
                            <p className="text-sm font-semibold">
                              {numberValue(
                                feed.ndf_pct,
                                1
                              )}
                              %
                            </p>
                          </div>

                          <div>
                            <p className="text-[9px] uppercase tracking-wider text-muted-foreground">
                              Starch
                            </p>
                            <p className="text-sm font-semibold">
                              {numberValue(
                                feed.starch_pct,
                                1
                              )}
                              %
                            </p>
                          </div>

                        </div>

                      </Card>

                    );
                  }
                )}

              </div>

            )}

          </div>

        </DialogContent>

      </Dialog>


      {/* ------------------------------------------------ */}
      {/* Feed price history                               */}
      {/* ------------------------------------------------ */}

      <Dialog
        open={
          priceHistoryOpen
        }
        onOpenChange={
          setPriceHistoryOpen
        }
      >

        <DialogContent className="max-w-xl">

          <DialogHeader>

            <DialogTitle>
              Price history
            </DialogTitle>

            <DialogDescription>
              {priceHistoryFeed?.name || "Feed"} — historical prices used in feed-cost reporting.
            </DialogDescription>

          </DialogHeader>


          {priceHistoryLoading ? (

            <div className="py-10 text-center text-sm text-muted-foreground">
              Loading price history…
            </div>

          ) : (

            <div className="space-y-4">

              <div className="rounded-xl border bg-primary/[0.025] p-4">

                <p className="text-xs text-muted-foreground">
                  Current price
                </p>

                <p className="font-display text-2xl font-bold text-[hsl(148,38%,16%)] mt-1">
                  {formatCurrency(
                    priceHistory?.current_price ??
                    priceHistoryFeed?.cost_per_tonne,
                    currency,
                    0
                  )}
                  <span className="text-xs text-muted-foreground font-normal">
                    {" "}/ tonne
                  </span>
                </p>

              </div>


              <div className="rounded-xl border overflow-hidden">

                <Table>

                  <TableHeader>

                    <TableRow>

                      <TableHead>
                        Effective from
                      </TableHead>

                      <TableHead className="text-right">
                        Price / tonne
                      </TableHead>

                    </TableRow>

                  </TableHeader>


                  <TableBody>

                    {(priceHistory?.prices || []).map(
                      (
                        record
                      ) => (

                        <TableRow
                          key={
                            record.id ||
                            `${record.effective_date}-${record.cost_per_tonne}`
                          }
                        >

                          <TableCell>
                            {record.effective_date
                              ? new Date(
                                  `${record.effective_date}T00:00:00`
                                ).toLocaleDateString(
                                  "en-IE",
                                  {
                                    day:
                                      "numeric",

                                    month:
                                      "short",

                                    year:
                                      "numeric",
                                  }
                                )
                              : "—"}
                          </TableCell>

                          <TableCell className="text-right font-semibold tabular-nums">
                            {formatCurrency(
                              record.cost_per_tonne,
                              currency,
                              0
                            )}
                          </TableCell>

                        </TableRow>

                      )
                    )}


                    {(priceHistory?.prices || []).length ===
                      0 && (

                      <TableRow>

                        <TableCell
                          colSpan={2}
                          className="py-8 text-center text-muted-foreground"
                        >
                          No price history recorded yet.
                        </TableCell>

                      </TableRow>

                    )}

                  </TableBody>

                </Table>

              </div>


              <p className="text-xs text-muted-foreground leading-relaxed">
                Feed-use reports value each day's feed at the price that was effective on that date. Changing today's price does not rewrite earlier periods.
              </p>

            </div>

          )}

        </DialogContent>

      </Dialog>


      {/* ------------------------------------------------ */}
      {/* Lab import dialog                                */}
      {/* ------------------------------------------------ */}

      <Dialog
        open={
          importOpen
        }
        onOpenChange={
          setImportOpen
        }
      >

        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">

          <DialogHeader>

            <DialogTitle>
              Import lab report
            </DialogTitle>

            <DialogDescription>

              {importStep ===
              "paste"
                ? "Upload a PDF laboratory report, or paste text/CSV. Farm Hand will map recognised nutrients for review."
                : `Recognised ${parsed?.matched_count || 0} values. Review, edit and save.`}

            </DialogDescription>

          </DialogHeader>


          {importStep ===
          "paste" ? (

            <div className="space-y-4">

              <label
                data-testid="lab-report-dropzone"
                className="flex flex-col items-center justify-center gap-2 border-2 border-dashed border-border rounded-xl p-6 cursor-pointer hover:border-primary hover:bg-secondary/40 transition-colors"
              >

                <FileText className="h-7 w-7 text-primary" />

                <span className="text-sm font-medium text-[hsl(148,38%,16%)]">

                  {uploading
                    ? "Reading PDF…"
                    : "Upload a PDF lab report"}

                </span>

                <span className="text-xs text-muted-foreground">
                  Click to choose a .pdf analysis report
                </span>

                <input
                  type="file"
                  accept="application/pdf,.pdf"
                  className="hidden"
                  data-testid="lab-report-file-input"
                  onChange={
                    handleFile
                  }
                  disabled={
                    uploading
                  }
                />

              </label>


              <div className="flex items-center gap-3 text-xs text-muted-foreground">

                <div className="h-px bg-border flex-1" />

                or paste text / CSV

                <div className="h-px bg-border flex-1" />

              </div>


              <Textarea
                data-testid="lab-report-textarea"
                value={
                  importText
                }
                onChange={(
                  event
                ) =>
                  setImportText(
                    event.target
                      .value
                  )
                }
                rows={9}
                className="font-mono text-xs"
                placeholder={
                  "Sample: Grass Silage\nDry Matter,%,28.5\nCrude Protein,% DM,13.2\nME,MJ/kg DM,11.1\npH,,4.2\n..."
                }
              />


              <DialogFooter>

                <Button
                  className="rounded-full"
                  data-testid="parse-report-button"
                  onClick={
                    runParse
                  }
                  disabled={
                    parsing ||
                    uploading
                  }
                >

                  {parsing
                    ? "Parsing…"
                    : "Parse pasted text"}

                </Button>

              </DialogFooter>

            </div>

          ) : (

            <div
              className="space-y-4"
              data-testid="import-review"
            >

              {parsed
                ?.confidence && (

                <div
                  className={`flex items-start gap-2 rounded-lg p-3 text-sm ${CONF[parsed.confidence].cls}`}
                  data-testid="extraction-confidence"
                >

                  <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full ${CONF[parsed.confidence].chip}`}>

                    {parsed.confidence} confidence

                  </span>

                  <span className="text-muted-foreground">
                    {parsed.confidence_note}
                  </span>

                </div>

              )}


              {parsed
                ?.source && (

                <p
                  className="text-xs text-muted-foreground"
                  data-testid="import-source-meta"
                >

                  Source:{" "}

                  <strong>
                    {parsed.source.filename}
                  </strong>

                  {" · "}

                  {parsed.source.pages} page(s)

                  {" · "}

                  {parsed.source.chars} characters extracted

                </p>

              )}


              <div>

                <Label className="text-xs">
                  Apply to
                </Label>

                <Select
                  value={
                    targetId
                  }
                  onValueChange={
                    setTargetId
                  }
                >

                  <SelectTrigger
                    data-testid="import-target-select"
                    className="mt-1"
                  >

                    <SelectValue />

                  </SelectTrigger>


                  <SelectContent>

                    <SelectItem value={
                      NEW
                    }>
                      Create new feed
                    </SelectItem>


                    {storedFeeds.map(
                      (
                        feed
                      ) => (

                        <SelectItem
                          key={
                            feed.id
                          }
                          value={
                            feed.id
                          }
                        >
                          Update: {feed.name}
                        </SelectItem>

                      )
                    )}

                  </SelectContent>

                </Select>

              </div>


              <p className="text-xs text-muted-foreground">
                Highlighted fields were filled from the report. Unmatched lines are saved in Notes.
              </p>


              <FeedFormFields
                form={
                  reviewForm
                }
                set={
                  setR
                }
                highlight={
                  highlight
                }
              />


              <DialogFooter className="gap-2">

                <Button
                  variant="ghost"
                  className="rounded-full"
                  onClick={() =>
                    setImportStep(
                      "paste"
                    )
                  }
                >
                  Back
                </Button>


                <Button
                  className="rounded-full"
                  data-testid="import-save-button"
                  onClick={
                    importSave
                  }
                >

                  {targetId ===
                  NEW
                    ? "Create feed"
                    : "Update feed"}

                </Button>

              </DialogFooter>

            </div>

          )}

        </DialogContent>

      </Dialog>


      {/* ------------------------------------------------ */}
      {/* Live grass                                       */}
      {/* ------------------------------------------------ */}

      {grazedGrass && (

        <Card
          className="mb-6 p-5 sm:p-6 border-primary/20 bg-primary/[0.035]"
          data-testid="live-grazed-grass-card"
        >

          <div className="flex flex-col xl:flex-row xl:items-center xl:justify-between gap-5">

            <div className="flex items-start gap-3 max-w-xl">

              <div className="h-12 w-12 rounded-full bg-primary/10 grid place-items-center shrink-0">

                <Leaf className="h-6 w-6 text-primary" />

              </div>


              <div>

                <div className="flex flex-wrap items-center gap-2">

                  <h3 className="font-display font-bold text-lg text-[hsl(148,38%,16%)]">
                    Live Grazed Grass inventory
                  </h3>

                  <Badge className="bg-primary/10 text-primary hover:bg-primary/10">
                    Automatically linked
                  </Badge>

                </div>


                <p className="text-sm text-muted-foreground mt-1 leading-relaxed">
                  Quantity comes from projected paddock covers. Nutrients come from the active Grass Quality analysis.
                </p>

              </div>

            </div>


            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 sm:gap-8">

              <div>

                <p className="text-muted-foreground text-xs">
                  Pasture DM
                </p>

                <p className="font-bold tabular-nums text-[hsl(148,38%,16%)] mt-1">
                  {numberValue(
                    grazedGrass.available_grass_kg_dm
                  )}{" "}
                  kg
                </p>

              </div>


              <div>

                <p className="text-muted-foreground text-xs">
                  Fresh pasture equivalent
                </p>

                <p className="font-bold tabular-nums text-[hsl(148,38%,16%)] mt-1">
                  {numberValue(
                    grazedGrass.tonnes_fresh,
                    2
                  )}{" "}
                  t
                </p>

              </div>


              <div>

                <p className="text-muted-foreground text-xs">
                  Dry matter
                </p>

                <p className="font-bold tabular-nums text-[hsl(148,38%,16%)] mt-1">
                  {numberValue(
                    grazedGrass.dm_pct,
                    1
                  )}
                  %
                </p>

              </div>


              <div>

                <p className="text-muted-foreground text-xs">
                  Growth source
                </p>

                <p className="font-bold text-[hsl(148,38%,16%)] mt-1">

                  {grazedGrass.growth_source ===
                  "weather"
                    ? "Weather forecast"
                    : "Fallback rate"}

                </p>

              </div>

            </div>

          </div>

        </Card>

      )}


      {/* ------------------------------------------------ */}
      {/* Stored feeds heading / filters                   */}
      {/* ------------------------------------------------ */}

      <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-4 mb-3">

        <div>

          <h2 className="font-display text-xl font-bold text-[hsl(148,38%,16%)]">
            Stored feeds
          </h2>

          <p className="text-sm text-muted-foreground mt-1">
            Click a feed to view its full nutritional profile and laboratory analysis.
          </p>

        </div>


        <div className="grid grid-cols-1 sm:grid-cols-[minmax(200px,1fr)_auto_auto] gap-2 w-full lg:w-auto">

          <div className="relative min-w-[220px]">

            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />

            <Input
              value={
                search
              }
              onChange={(
                event
              ) =>
                setSearch(
                  event.target
                    .value
                )
              }
              placeholder="Search feeds..."
              className="pl-9 rounded-full"
            />

          </div>


          <Select
            value={
              stockFilter
            }
            onValueChange={
              setStockFilter
            }
          >

            <SelectTrigger className="rounded-full min-w-[130px]">

              <Filter className="h-4 w-4 mr-2" />

              <SelectValue />

            </SelectTrigger>


            <SelectContent>

              <SelectItem value="all">
                All feeds
              </SelectItem>

              <SelectItem value="stock">
                In stock
              </SelectItem>

              <SelectItem value="empty">
                Out of stock
              </SelectItem>

            </SelectContent>

          </Select>


          <Select
            value={
              sortMode
            }
            onValueChange={
              setSortMode
            }
          >

            <SelectTrigger className="rounded-full min-w-[160px]">

              <SelectValue />

            </SelectTrigger>


            <SelectContent>

              <SelectItem value="name">
                Sort: Name A–Z
              </SelectItem>

              <SelectItem value="stock">
                Sort: Stock high–low
              </SelectItem>

              <SelectItem value="cost">
                Sort: Cost high–low
              </SelectItem>

            </SelectContent>

          </Select>

        </div>

      </div>


      {/* ------------------------------------------------ */}
      {/* Desktop / tablet table                           */}
      {/* ------------------------------------------------ */}

      <Card className="hidden md:block overflow-x-auto">

        <Table data-testid="feed-table">

          <TableHeader>

            <TableRow>

              <TableHead className="w-8" />

              <TableHead>
                Feed
              </TableHead>

              <TableHead className="text-right">
                Tonnes fresh
              </TableHead>

              <TableHead className="text-right">
                Dry matter
              </TableHead>

              <TableHead className="text-right">
                Crude protein
              </TableHead>

              <TableHead className="text-right">
                ME
              </TableHead>

              <TableHead className="text-right">
                NDF
              </TableHead>

              <TableHead className="text-right">
                Starch
              </TableHead>

              <TableHead className="text-right">
                Cost / tonne
              </TableHead>

              <TableHead className="text-right">
                Actions
              </TableHead>

            </TableRow>

          </TableHeader>


          <TableBody>

            {filteredFeeds.length ===
              0 && (

              <TableRow>

                <TableCell
                  colSpan={10}
                  className="text-center text-muted-foreground py-12"
                >
                  No feeds match the current search or filter.
                </TableCell>

              </TableRow>

            )}


            {filteredFeeds.map(
              (
                feed,
                index
              ) => {
                const style =
                  categoryStyle(
                    feed
                  );


                return (
                  <Fragment key={
                    feed.id
                  }>

                    <TableRow
                      className={
                        index %
                          2
                          ? "bg-secondary/25"
                          : ""
                      }
                      data-testid={`feed-row-${feed.id}`}
                    >

                      <TableCell>

                        <button
                          data-testid={`feed-expand-${feed.id}`}
                          onClick={() =>
                            toggle(
                              feed.id
                            )
                          }
                          className="text-muted-foreground hover:text-primary"
                        >

                          {expanded[
                            feed.id
                          ] ? (
                            <ChevronDown className="h-4 w-4" />
                          ) : (
                            <ChevronRight className="h-4 w-4" />
                          )}

                        </button>

                      </TableCell>


                      <TableCell className="font-medium">

                        <div className="flex items-center gap-3">

                          <div className={`h-8 w-8 rounded-full grid place-items-center shrink-0 ${style.icon}`}>

                            <Wheat className="h-4 w-4" />

                          </div>


                          <div>

                            <div className="flex flex-wrap items-center gap-1.5">

                              <span>
                                {feed.name}
                              </span>


                              {feed.category && (

                                <Badge
                                  variant="secondary"
                                  className={`text-[10px] border-none ${style.chip}`}
                                >
                                  {feed.category}
                                </Badge>

                              )}


                              {hasAdvanced(
                                feed
                              ) && (

                                <Badge className="text-[10px] bg-primary/10 text-primary hover:bg-primary/10">
                                  Lab
                                </Badge>

                              )}

                            </div>


                            <p className="text-[10px] text-muted-foreground mt-0.5">

                              {Number(
                                feed.tonnes_fresh
                              ) > 0
                                ? "In stock"
                                : "Stored feed"}

                            </p>

                          </div>

                        </div>

                      </TableCell>


                      <TableCell className="text-right tabular-nums">
                        {numberValue(
                          feed.tonnes_fresh,
                          1
                        )}
                      </TableCell>


                      <TableCell className="text-right tabular-nums">
                        {numberValue(
                          feed.dm_pct,
                          1
                        )}
                        %
                      </TableCell>


                      <TableCell className="text-right tabular-nums">
                        {numberValue(
                          feed.cp_pct,
                          1
                        )}
                        %
                      </TableCell>


                      <TableCell className="text-right tabular-nums">
                        {numberValue(
                          feed.me,
                          1
                        )}
                      </TableCell>


                      <TableCell className="text-right tabular-nums">
                        {numberValue(
                          feed.ndf_pct,
                          1
                        )}
                        %
                      </TableCell>


                      <TableCell className="text-right tabular-nums">
                        {numberValue(
                          feed.starch_pct,
                          1
                        )}
                        %
                      </TableCell>


                      <TableCell className="text-right tabular-nums">

                        {formatCurrency(
                          feed.cost_per_tonne,
                          currency,
                          0
                        )}

                      </TableCell>


                      <TableCell className="text-right whitespace-nowrap">

                        <Button
                          variant="ghost"
                          size="icon"
                          title="Price history"
                          onClick={() =>
                            openFeedPriceHistory(
                              feed
                            )
                          }
                        >

                          <History className="h-4 w-4" />

                        </Button>


                        <Button
                          variant="ghost"
                          size="icon"
                          data-testid={`feed-edit-${feed.id}`}
                          onClick={() =>
                            openEdit(
                              feed
                            )
                          }
                          title="Edit feed"
                        >

                          <Pencil className="h-4 w-4" />

                        </Button>


                        <Button
                          variant="ghost"
                          size="icon"
                          className="text-destructive"
                          data-testid={`feed-delete-${feed.id}`}
                          title="Delete feed"
                          onClick={() =>
                            window.confirm(
                              "Delete this feed?"
                            ) &&
                            del.mutate(
                              feed.id
                            )
                          }
                        >

                          <Trash2 className="h-4 w-4" />

                        </Button>

                      </TableCell>

                    </TableRow>


                    {expanded[
                      feed.id
                    ] && (

                      <TableRow className="bg-accent/40">

                        <TableCell
                          colSpan={10}
                          className="p-0"
                        >

                          <div
                            className="p-5"
                            data-testid={`feed-advanced-${feed.id}`}
                          >

                            <div className="flex items-center gap-2 mb-4">

                              <FlaskConical className="h-4 w-4 text-primary" />

                              <p className="font-display font-bold text-sm text-[hsl(148,38%,16%)]">
                                Laboratory analysis — {feed.name}
                              </p>

                            </div>


                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-x-8 gap-y-5">

                              {ADV_GROUPS.map(
                                (
                                  group
                                ) => (

                                  <div key={
                                    group.title
                                  }>

                                    <p className="text-[10px] font-semibold uppercase tracking-[0.15em] text-muted-foreground mb-2">
                                      {group.title}
                                    </p>


                                    <div className="space-y-1">

                                      {group.fields.map(
                                        (
                                          field
                                        ) => (

                                          <div
                                            key={
                                              field.k
                                            }
                                            className="flex items-center justify-between text-sm gap-3"
                                          >

                                            <span className="text-muted-foreground">
                                              {field.label}
                                            </span>

                                            <span className="tabular-nums font-medium text-[hsl(148,38%,16%)]">

                                              {val(
                                                feed[
                                                  field.k
                                                ]
                                              )}

                                              {feed[
                                                field.k
                                              ] !=
                                                null &&
                                              feed[
                                                field.k
                                              ] !==
                                                "" &&
                                              field.unit
                                                ? ` ${field.unit}`
                                                : ""}

                                            </span>

                                          </div>

                                        )
                                      )}

                                    </div>

                                  </div>

                                )
                              )}

                            </div>


                            {feed.notes && (

                              <p className="text-xs text-muted-foreground mt-4 whitespace-pre-line italic">
                                Notes: {feed.notes}
                              </p>

                            )}

                          </div>

                        </TableCell>

                      </TableRow>

                    )}

                  </Fragment>
                );
              }
            )}

          </TableBody>

        </Table>

      </Card>


      {/* ------------------------------------------------ */}
      {/* Phone cards                                      */}
      {/* ------------------------------------------------ */}

      <div className="md:hidden space-y-3">

        {filteredFeeds.length ===
          0 && (

          <Card className="p-6 text-center text-sm text-muted-foreground">
            No feeds match the current search or filter.
          </Card>

        )}


        {filteredFeeds.map(
          (
            feed
          ) => {
            const style =
              categoryStyle(
                feed
              );


            return (
              <Card
                key={
                  feed.id
                }
                className="overflow-hidden"
              >

                <button
                  type="button"
                  onClick={() =>
                    toggle(
                      feed.id
                    )
                  }
                  className="w-full p-4 text-left"
                >

                  <div className="flex items-start justify-between gap-3">

                    <div className="flex items-center gap-3 min-w-0">

                      <div className={`h-10 w-10 rounded-full grid place-items-center shrink-0 ${style.icon}`}>

                        <Wheat className="h-5 w-5" />

                      </div>


                      <div className="min-w-0">

                        <div className="flex flex-wrap items-center gap-1.5">

                          <p className="font-semibold text-[hsl(148,38%,16%)]">
                            {feed.name}
                          </p>


                          {feed.category && (

                            <Badge className={`text-[9px] border-none ${style.chip}`}>
                              {feed.category}
                            </Badge>

                          )}

                        </div>


                        <p className="text-xs text-muted-foreground mt-1">

                          {numberValue(
                            feed.tonnes_fresh,
                            1
                          )}{" "}

                          t fresh ·{" "}

                          {numberValue(
                            feed.dm_pct,
                            1
                          )}
                          % DM

                        </p>

                      </div>

                    </div>


                    {expanded[
                      feed.id
                    ] ? (

                      <ChevronDown className="h-5 w-5 text-muted-foreground shrink-0" />

                    ) : (

                      <ChevronRight className="h-5 w-5 text-muted-foreground shrink-0" />

                    )}

                  </div>


                  <div className="grid grid-cols-4 gap-2 mt-4">

                    <div>

                      <p className="text-[9px] uppercase tracking-wider text-muted-foreground">
                        CP
                      </p>

                      <p className="text-sm font-semibold mt-0.5">
                        {numberValue(
                          feed.cp_pct,
                          1
                        )}
                        %
                      </p>

                    </div>


                    <div>

                      <p className="text-[9px] uppercase tracking-wider text-muted-foreground">
                        ME
                      </p>

                      <p className="text-sm font-semibold mt-0.5">
                        {numberValue(
                          feed.me,
                          1
                        )}
                      </p>

                    </div>


                    <div>

                      <p className="text-[9px] uppercase tracking-wider text-muted-foreground">
                        NDF
                      </p>

                      <p className="text-sm font-semibold mt-0.5">
                        {numberValue(
                          feed.ndf_pct,
                          1
                        )}
                        %
                      </p>

                    </div>


                    <div>

                      <p className="text-[9px] uppercase tracking-wider text-muted-foreground">
                        Cost
                      </p>

                      <p className="text-sm font-semibold mt-0.5">
                        {formatCurrency(
                          feed.cost_per_tonne,
                          currency,
                          0
                        )}
                      </p>

                    </div>

                  </div>

                </button>


                {expanded[
                  feed.id
                ] && (

                  <div className="border-t p-4 bg-secondary/20">

                    <div className="flex items-center justify-between gap-3 mb-4">

                      <div className="flex items-center gap-2">

                        <FlaskConical className="h-4 w-4 text-primary" />

                        <p className="font-semibold text-sm">
                          Nutritional profile
                        </p>

                      </div>


                      <div className="flex gap-1">

                        <Button
                          variant="ghost"
                          size="icon"
                          title="Price history"
                          onClick={() =>
                            openFeedPriceHistory(
                              feed
                            )
                          }
                        >

                          <History className="h-4 w-4" />

                        </Button>


                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() =>
                            openEdit(
                              feed
                            )
                          }
                        >

                          <Pencil className="h-4 w-4" />

                        </Button>


                        <Button
                          variant="ghost"
                          size="icon"
                          className="text-destructive"
                          onClick={() =>
                            window.confirm(
                              "Delete this feed?"
                            ) &&
                            del.mutate(
                              feed.id
                            )
                          }
                        >

                          <Trash2 className="h-4 w-4" />

                        </Button>

                      </div>

                    </div>


                    <div className="grid grid-cols-2 gap-x-6 gap-y-5">

                      {ADV_GROUPS.map(
                        (
                          group
                        ) => (

                          <div key={
                            group.title
                          }>

                            <p className="text-[9px] font-semibold uppercase tracking-[0.14em] text-muted-foreground mb-2">
                              {group.title}
                            </p>


                            <div className="space-y-1.5">

                              {group.fields.map(
                                (
                                  field
                                ) => (

                                  <div
                                    key={
                                      field.k
                                    }
                                    className="flex justify-between gap-2 text-xs"
                                  >

                                    <span className="text-muted-foreground">
                                      {field.label}
                                    </span>

                                    <span className="font-medium tabular-nums">

                                      {val(
                                        feed[
                                          field.k
                                        ]
                                      )}

                                      {feed[
                                        field.k
                                      ] !=
                                        null &&
                                      feed[
                                        field.k
                                      ] !==
                                        "" &&
                                      field.unit
                                        ? ` ${field.unit}`
                                        : ""}

                                    </span>

                                  </div>

                                )
                              )}

                            </div>

                          </div>

                        )
                      )}

                    </div>


                    {feed.notes && (

                      <p className="text-xs text-muted-foreground mt-4 whitespace-pre-line">
                        {feed.notes}
                      </p>

                    )}

                  </div>

                )}

              </Card>
            );
          }
        )}

      </div>


      <p className="text-[11px] text-muted-foreground mt-3">

        Showing {filteredFeeds.length} of {storedFeeds.length} stored feeds

        {" · "}

        Currency: {currencySymbol}

      </p>

    </div>
  );
}